import * as React from 'react';
import { cn } from '../cn';
import { Skeleton } from './skeleton';

export interface TableSkeletonColumn {
  width?: string;
  align?: 'left' | 'right' | 'center';
}

export interface TableSkeletonProps {
  /** Număr de rânduri afișate. Default 8. */
  rows?: number;
  /** Configurare per coloană: width (procente) și align. Default 5 coloane uniform. */
  columns?: TableSkeletonColumn[];
  /** Afișează un rând placeholder pentru thead. Default true. */
  showHeader?: boolean;
  /** className extra pe container-ul principal */
  className?: string;
  /** Înălțime rând (px). Default 48 (potrivit cu density „cozy") */
  rowHeight?: number;
}

const DEFAULT_COLUMNS: TableSkeletonColumn[] = [
  { width: '20%' },
  { width: '20%' },
  { width: '20%' },
  { width: '20%' },
  { width: '20%', align: 'right' },
];

/**
 * Schelet de tabel — match cu structura `<table>` reală pentru tranziție smooth
 * la hidratare. Fiecare celulă are un Skeleton bar cu width random (60-90%) și
 * stagger pe diagonală (rândul i × coloana j × 40ms) pentru valură organică.
 *
 * Aesthetic intent: refined / minimal — placeholder mimetic, fără branding.
 * Inspirație: LinkedIn (table rows), GitHub (file lists).
 */
export function TableSkeleton({
  rows = 8,
  columns = DEFAULT_COLUMNS,
  showHeader = true,
  className,
  rowHeight = 48,
}: TableSkeletonProps) {
  // Determinist „pseudo-random" width per (row, col) — același la fiecare render
  const widthFor = (r: number, c: number): string => {
    const seed = (r * 7 + c * 13) % 5;
    return ['62%', '74%', '88%', '52%', '78%'][seed]!;
  };

  return (
    <div
      role="status"
      aria-label="Se încarcă..."
      className={cn(
        'w-full divide-y divide-neutral-100 dark:divide-neutral-800',
        className,
      )}
    >
      {showHeader && (
        <div
          className="grid items-center px-4 py-2.5 bg-neutral-50/50 dark:bg-neutral-900/50"
          style={{ gridTemplateColumns: columns.map((c) => c.width || '1fr').join(' '), gap: '12px' }}
        >
          {columns.map((c, i) => (
            <div key={i} className={c.align === 'right' ? 'flex justify-end' : c.align === 'center' ? 'flex justify-center' : ''}>
              <Skeleton width="60%" height={10} rounded="sm" delay={i * 30} />
            </div>
          ))}
        </div>
      )}
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="grid items-center px-4"
          style={{
            gridTemplateColumns: columns.map((c) => c.width || '1fr').join(' '),
            gap: '12px',
            height: rowHeight,
          }}
        >
          {columns.map((c, ci) => (
            <div key={ci} className={c.align === 'right' ? 'flex justify-end' : c.align === 'center' ? 'flex justify-center' : ''}>
              <Skeleton
                width={widthFor(r, ci)}
                height={12}
                rounded="md"
                delay={(r * 40 + ci * 30) % 600}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
